import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { isFieldFilterValid } from 'module/types/filter-field/grouped-filter/utils';
import {
  getGroupFilterTransformer,
  GroupFieldsMeta,
} from 'module/ui/components/molecules/field-filter/filter-transformation/filter-transformer';
import { FilterFunction } from 'module/ui/components/molecules/field-filter/types';
import { getGroupedFilterFunction } from 'module/ui/components/molecules/field-filter/utils';
import { SignalType } from 'api/signal/types';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearch } from '../use-search';

export const queueMacrotask = (task: (arg?: any) => void) => {
  window.setTimeout(task, 0);
};

interface SignalFilterParams {
  signalConfigurationId?: number;
  signalType?: SignalType;
  myAccountSignalType?: SignalType;
  companyId?: number;
  selectedSignals?: string[];
}

interface UseGroupFiltersReturn {
  selectFilter: (next: GroupedFilter) => void;
  replaceFilter: (next: GroupedFilter) => void;
  clearFilters: () => void;
  filter: GroupedFilter;
  filterFunction: FilterFunction | null;
  signalFilterParams: SignalFilterParams;
  setSignalFilterParams: (params: SignalFilterParams) => void;
}

export const useGroupFilters = ({
  groupFieldsMeta,
  defaultGroup,
  canSetFilter = false,
}: {
  groupFieldsMeta: GroupFieldsMeta;
  defaultGroup: FilterGroupType;
  canSetFilter?: boolean;
}): UseGroupFiltersReturn => {
  const transformer = useMemo(
    () => getGroupFilterTransformer(groupFieldsMeta, defaultGroup),
    [groupFieldsMeta, defaultGroup],
  );
  const [search, setSearch] = useSearch(transformer);

  const [filter, setFilter] = useState(search);
  const [signalFilterParams, setSignalFilterParams] =
    useState<SignalFilterParams>({});

  const selectFilter = useCallback(
    (new_filter: GroupedFilter) => {
      if (!canSetFilter) {
        return;
      }

      /* TODO:: This approach of setting two state is not scalable. We need to migrate to single state directly from URL. */
      setFilter(new_filter);
      setSearch(new_filter);
    },
    [canSetFilter, setFilter, setSearch],
  );
  const replaceFilter = useCallback(
    (new_filter: GroupedFilter) => {
      setFilter(new_filter);
      queueMacrotask(() => setSearch(new_filter, true));
    },
    [setFilter, setSearch],
  );
  const filterFunction = useMemo(
    () => getGroupedFilterFunction(filter),
    [filter],
  );
  const clearFilters = useCallback(() => {
    selectFilter(defaultGroupedFilter(FilterGroupType.Task));
    setSignalFilterParams({});
  }, [selectFilter]);

  useEffect(() => {
    if (
      !isEqual(
        {
          type: search.type,
          defaultGroup: search.defaultGroup,
          groupCriteria: search.groupCriteria,
        },
        {
          type: filter.type,
          defaultGroup: filter.defaultGroup,
          groupCriteria: filter.groupCriteria.filter(({ criteria }) =>
            isFieldFilterValid(criteria),
          ),
        },
      )
    ) {
      setFilter(search);
    }
  }, [search, filter, setFilter]);

  return {
    selectFilter,
    replaceFilter,
    clearFilters,
    filter,
    filterFunction,
    signalFilterParams,
    setSignalFilterParams,
  };
};
